import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import type { WorkflowBuildNotification } from '~/api/hermes/typedefs.autogen';
import type {
  AiWorkflowBuild,
  AiWorkflowBuildStatus,
  AiWorkflowBuildToastId,
} from '~/redux/slices/ai-workflow-builds/types';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { useNotificationCenterActions } from '~/features/notifications/NotificationCenterContext';
import { hermes } from '~/redux/api/hermes';
import { slices } from '~/redux/slices';

import { useAiWorkflowBuildMonitor } from './useAiWorkflowBuildMonitor';

export type AiWorkflowBuildNotification = AiWorkflowBuild;

const WORKFLOW_BUILD_NOTIFICATION_LIMIT = 100;
const HANDLED_ERROR_QUERY_OPTIONS = { showErrorToast: false };
const TERMINAL_STATUSES: ReadonlySet<AiWorkflowBuildStatus> = new Set([
  'failed',
  'ready',
]);

const FE_STATUS_BY_SERVER_STATUS: Record<
  string,
  AiWorkflowBuildStatus | undefined
> = {
  failed: 'failed',
  FAILED: 'failed',
  INVOKING: 'running',
  pending_scan: 'pending_scan',
  PENDING_SCAN: 'pending_scan',
  queued: 'queued',
  QUEUED: 'queued',
  ready: 'ready',
  running: 'running',
  RUNNING: 'running',
  SUCCEEDED: 'ready',
};

const normalizeAiBuildStatus = (status: string): AiWorkflowBuildStatus =>
  FE_STATUS_BY_SERVER_STATUS[status] ?? 'running';

const mapNotification = ({
  build_id,
  conversation_id,
  created_at,
  error,
  read_at,
  status,
  title,
  updated_at,
  workflow_id,
}: WorkflowBuildNotification): AiWorkflowBuildNotification => ({
  conversation_id,
  build_id,
  status: normalizeAiBuildStatus(status),
  workflow_id: workflow_id ?? null,
  error: error ?? null,
  title: title ?? null,
  is_read: read_at != null,
  created_at,
  updated_at,
});

export const useAiWorkflowBuildNotificationActions = () => {
  const dispatch = useDispatch();
  const { open } = useNotificationCenterActions();

  const addNotification = useCallback(
    (notification: AiWorkflowBuildNotification) => {
      dispatch(slices.aiWorkflowBuilds.actions.addBuild(notification));
    },
    [dispatch],
  );

  return { addNotification, openNotifications: open };
};

export const useAiWorkflowBuildNotifications = () => {
  const dispatch = useDispatch();
  const { dismiss } = useToast();
  const dismissedStartedToastIdsRef = useRef(new Set<AiWorkflowBuildToastId>());
  const { data } =
    hermes.endpoints.listWorkflowBuildNotificationsEndpointV1WorkflowBuildNotificationsGet.useQuery(
      {
        limit: WORKFLOW_BUILD_NOTIFICATION_LIMIT,
        offset: 0,
        rtkQueryOptions: HANDLED_ERROR_QUERY_OPTIONS,
      },
    );
  const [updateNotification] =
    hermes.endpoints.updateWorkflowBuildNotificationEndpointV1WorkflowBuildNotificationsBuildIdPatch.useMutation();
  const notifications = useSelector(
    slices.aiWorkflowBuilds.selectors.selectBuilds,
  );
  const notificationCount = useSelector(
    slices.aiWorkflowBuilds.selectors.selectBuildCount,
  );

  useEffect(() => {
    if (!data) return;
    dispatch(
      slices.aiWorkflowBuilds.actions.hydrateBuilds(
        data.notifications.map(mapNotification),
      ),
    );
  }, [data, dispatch]);

  useEffect(() => {
    notifications.forEach(({ started_toast_id, status }) => {
      if (
        started_toast_id == null ||
        !TERMINAL_STATUSES.has(status) ||
        dismissedStartedToastIdsRef.current.has(started_toast_id)
      ) {
        return;
      }

      dismiss(started_toast_id);
      dismissedStartedToastIdsRef.current.add(started_toast_id);
    });
  }, [dismiss, notifications]);

  const dismissNotification = useCallback(
    async (build_id: string) => {
      await updateNotification({
        buildId: build_id,
        updateWorkflowBuildNotificationRequest: { dismissed: true },
      }).unwrap();
      dispatch(slices.aiWorkflowBuilds.actions.dismissBuild(build_id));
    },
    [dispatch, updateNotification],
  );

  const markNotificationRead = useCallback(
    async (build_id: string) => {
      await updateNotification({
        buildId: build_id,
        updateWorkflowBuildNotificationRequest: { read: true },
      }).unwrap();
      dispatch(slices.aiWorkflowBuilds.actions.markBuildRead(build_id));
    },
    [dispatch, updateNotification],
  );

  return {
    dismissNotification,
    markNotificationRead,
    notifications,
    notificationCount,
  };
};

export const AiWorkflowBuildNotificationSubscription = ({
  notification,
}: {
  notification: AiWorkflowBuildNotification;
}) => {
  useAiWorkflowBuildMonitor(notification);
  return null;
};
